/**
 * Plan & Calendar domain vocabulary — the metric-kind schema, workout types,
 * recipes and the two display formatters, ported verbatim from the design
 * kit's `plan-and-clander design/reference/shared/plan-data.jsx`.
 *
 * KEEP IN SYNC with the API copy at
 * packages/api/src/utils/planShared.ts — same file, both sides, so the API's
 * notification copy reads identically to the target sheet built here.
 *
 * A target/actual is a SPARSE map of metric-kind → number. Any subset of keys
 * is valid and `{}` is meaningful (an "open" target). Never model distance or
 * duration as separate exercise types.
 */

export type MetricKind = 'sets' | 'reps' | 'weight' | 'dist' | 'time' | 'rounds' | 'hold' | 'cal';

export type TargetMap = Partial<Record<MetricKind, number>>;

export interface MetricKindDef {
  label: string;
  unit: string;
  step: number;
  default: number;
}

export const METRIC_KINDS: Record<MetricKind, MetricKindDef> = {
  sets: { label: 'Sets', unit: 'sets', step: 1, default: 3 },
  reps: { label: 'Reps', unit: 'reps', step: 1, default: 10 },
  weight: { label: 'Weight', unit: 'kg', step: 2.5, default: 40 },
  dist: { label: 'Distance', unit: 'km', step: 0.5, default: 5 },
  time: { label: 'Duration', unit: 'min', step: 5, default: 25 },
  rounds: { label: 'Rounds', unit: 'rounds', step: 1, default: 5 },
  hold: { label: 'Hold', unit: 'sec', step: 15, default: 60 },
  cal: { label: 'Calories', unit: 'kcal', step: 25, default: 300 },
};

export const METRIC_ORDER: MetricKind[] = ['sets', 'reps', 'weight', 'dist', 'time', 'rounds', 'hold', 'cal'];

export type WorkoutType = 'strength' | 'body' | 'run' | 'erg' | 'circuit' | 'hold' | 'flow';

export interface WorkoutTypeDef {
  label: string;
  /** Which metric kinds are relevant — the target sheet renders exactly these. */
  metricKeys: MetricKind[];
  /** The two or three the UI leads with (raise_target multiplies the first). */
  lead: MetricKind[];
}

export const WORKOUT_TYPES: Record<WorkoutType, WorkoutTypeDef> = {
  strength: { label: 'Strength', metricKeys: ['sets', 'weight', 'reps'], lead: ['sets', 'weight', 'reps'] },
  body: { label: 'Bodyweight', metricKeys: ['sets', 'reps', 'time'], lead: ['sets', 'reps'] },
  run: { label: 'Run', metricKeys: ['dist', 'time', 'cal'], lead: ['dist', 'time'] },
  erg: { label: 'Erg / machine', metricKeys: ['dist', 'time', 'cal'], lead: ['dist', 'time'] },
  circuit: { label: 'Circuit', metricKeys: ['rounds', 'time'], lead: ['rounds', 'time'] },
  hold: { label: 'Hold', metricKeys: ['hold', 'sets'], lead: ['hold'] },
  flow: { label: 'Mobility / flow', metricKeys: ['time'], lead: ['time'] },
};

/** 'adhoc' is system-assigned to plans created by "start an activity" — valid on a plan, never pickable. */
export type RecipeKey = 'rot' | 'ppl' | 'ul' | 'full' | 'hyb' | 'stn' | 'run' | 'blank' | 'adhoc';

export interface RecipeDef {
  key: RecipeKey;
  name: string;
  sub: string;
  /** 7-slot focus template, repeated across the horizon. 'Rest' slots are rest days. */
  days: string[];
  tag?: string;
}

/** Named recipe templates — common gym-floor splits, never licensed formats. */
export const RECIPES: RecipeDef[] = [
  { key: 'rot', name: 'Muscle rotation', sub: '6 days · one group a day', days: ['Arms', 'Chest', 'Legs', 'Back', 'Shoulders', 'Core', 'Rest'], tag: 'Most picked' },
  { key: 'ppl', name: 'Push · Pull · Legs', sub: '6 days · cycle runs twice', days: ['Push', 'Pull', 'Legs', 'Push', 'Pull', 'Legs', 'Rest'] },
  { key: 'ul', name: 'Upper / Lower', sub: '4 days · 3 rest days', days: ['Upper', 'Lower', 'Rest', 'Upper', 'Lower', 'Rest', 'Rest'] },
  { key: 'full', name: 'Full body 3×', sub: 'Mon · Wed · Fri', days: ['Full body', 'Rest', 'Full body', 'Rest', 'Full body', 'Rest', 'Rest'] },
  { key: 'hyb', name: 'Hybrid engine', sub: 'Strength + erg + sled', days: ['Strength', 'Engine', 'Legs', 'Engine', 'Strength', 'Long effort', 'Rest'] },
  { key: 'stn', name: 'Station racer', sub: '8 stations + runs · sim on day 6', days: ['Run + stations', 'Race strength', 'Rest', 'Erg engine', 'Run + stations', 'Race sim', 'Rest'], tag: 'Race prep' },
  { key: 'run', name: '5K build', sub: '3 runs · 2 strength', days: ['Easy run', 'Strength', 'Intervals', 'Rest', 'Strength', 'Long run', 'Rest'] },
  { key: 'blank', name: 'Start blank', sub: 'Build day by day', days: ['Rest', 'Rest', 'Rest', 'Rest', 'Rest', 'Rest', 'Rest'] },
];

export const RECIPE_KEYS = RECIPES.map(r => r.key);
export const WORKOUT_TYPE_KEYS = Object.keys(WORKOUT_TYPES) as WorkoutType[];
export const HORIZON_DAYS = [3, 7, 14, 30];

/**
 * Chip form: "4 × 10 · 30 kg", "5 km · 25 min", "Open — no target".
 * Ported verbatim from plan-data.jsx `fmtTarget`.
 */
export function fmtTarget(t: TargetMap | null | undefined): string {
  if (!t) return 'Open — no target';
  const p: string[] = [];
  if (t.sets && t.reps) p.push(`${t.sets} × ${t.reps}`);
  else if (t.reps) p.push(`${t.reps} reps`);
  else if (t.sets) p.push(`${t.sets} sets`);
  if (t.weight) p.push(`${t.weight} kg`);
  if (t.dist) p.push(`${t.dist} km`);
  if (t.time) p.push(`${t.time} min`);
  if (t.hold) p.push(`${t.hold} sec`);
  if (t.rounds) p.push(`${t.rounds} rounds`);
  if (t.cal) p.push(`${t.cal} kcal`);
  return p.length ? p.join(' · ') : 'Open — no target';
}

/**
 * Read-back form: "Treadmill run 5 km in under 25 min",
 * "Deadlift — 5 sets of 3 at 140 kg", "Plank — hold 90 sec".
 * Ported verbatim from plan-data.jsx `sentence`.
 */
export function sentence(n: string, t: TargetMap | null | undefined): string {
  if (!t || !Object.keys(t).length) return `${n} — open, log whatever you do`;
  if (t.dist && t.time) return `${n} ${t.dist} km in under ${t.time} min`;
  if (t.dist) return `${n} ${t.dist} km`;
  if (t.rounds && t.time) return `${t.rounds} rounds of ${n} in ${t.time} min`;
  if (t.sets && t.reps && t.weight) return `${n} — ${t.sets} sets of ${t.reps} at ${t.weight} kg`;
  if (t.sets && t.reps) return `${n} — ${t.sets} sets of ${t.reps}`;
  if (t.hold) return `${n} — hold ${t.hold} sec`;
  if (t.time) return `${n} for ${t.time} min`;
  return `${n} — ${fmtTarget(t)}`;
}

/**
 * Validate a sparse target/actual map: only known metric-kind keys, finite
 * non-negative numeric values. `{}` is valid (open target).
 */
export function isValidTargetMap(v: unknown): v is TargetMap {
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return false;
  for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
    if (!(k in METRIC_KINDS)) return false;
    if (typeof val !== 'number' || !Number.isFinite(val) || val < 0) return false;
  }
  return true;
}

/** Round a value to the metric's step (used by raise_target: ×1.05, snapped). */
export function roundToStep(kind: MetricKind, value: number): number {
  const step = METRIC_KINDS[kind].step;
  const snapped = Math.round(value / step) * step;
  // Avoid float dust (2.5-steps): fix to the step's decimal places.
  const decimals = (String(step).split('.')[1] || '').length;
  return Number(snapped.toFixed(decimals));
}

/** The leading metric of an item's target for its workout type, if present. */
export function leadingMetric(workoutType: WorkoutType, target: TargetMap): MetricKind | null {
  const def = WORKOUT_TYPES[workoutType];
  if (!def) return null;
  for (const k of def.lead) if (target[k] != null) return k;
  for (const k of METRIC_ORDER) if (target[k] != null) return k;
  return null;
}
